import { useState, useEffect, useCallback } from 'react';
import workflowService from '../services/api/workflowService';
import Module1Service from '../services/module1Service';

// Define DatabaseClient interface locally since it's not exported from workflowService
interface DatabaseClient {
  client_id: number;
  name: string;
  client_code: string;
  active: boolean;
}

// Define the return type for our hook
interface ApiModeHookResult {
  isUsingDatabaseApproach: boolean;
  setIsUsingDatabaseApproach: (value: boolean) => void;
  dbClientId: number | null;
  setDbClientId: (id: number | null) => void;
  getClients: () => Promise<string[] | DatabaseClient[]>;
  getWorkflows: (clientId: string | number) => Promise<any[]>;
  clientIdToString: (clientId: string | number) => string;
  getWorkflowIdParam: (workflow: any) => string;
  convertDbClientsToIds: (clients: any) => string[];
}

/**
 * Custom hook to manage the database-first API mode (PsychoDB mode)
 * This hook is now configured to always use the database approach
 */
export default function useApiMode(): ApiModeHookResult {
  // Always use database-first approach
  const [isUsingDatabaseApproach] = useState(true);
  
  const [dbClientId, setDbClientId] = useState<number | null>(() => {
    // Try to load from localStorage
    const storedId = localStorage.getItem('db_client_id');
    return storedId ? parseInt(storedId, 10) : null;
  });

  // No-op function for backward compatibility
  const setIsUsingDatabaseApproach = useCallback(() => {
    // This function is kept for backward compatibility but does nothing
    // since we always use database approach now
  }, []);

  // Effect to listen for changes in db_client_id from localStorage
  useEffect(() => {
    // Function to handle storage events for db_client_id only
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'db_client_id' && event.newValue) {
        setDbClientId(parseInt(event.newValue, 10));
      }
    };

    // Create a custom storage event dispatcher for same-page communication
    const updateLocalStorage = () => {
      const storedClientId = localStorage.getItem('db_client_id');
      setDbClientId(storedClientId ? parseInt(storedClientId, 10) : null);
    };

    // Listen for storage events from other tabs/windows
    window.addEventListener('storage', handleStorageChange);
    
    // Create a custom event for same-page communication
    window.addEventListener('local-storage-updated', updateLocalStorage);

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('local-storage-updated', updateLocalStorage);
    };
  }, []);

  useEffect(() => {
    if (dbClientId !== null) {
      localStorage.setItem('db_client_id', dbClientId.toString());
    }
    
    // Dispatch event for other components using this hook
    window.dispatchEvent(new Event('local-storage-updated'));
  }, [dbClientId]);

  /**
   * Get clients using database approach only
   * Memoized to prevent unnecessary re-renders and API call loops
   */
  const getClients = useCallback(async () => {
    console.log('Getting clients in database mode');
    
    try {
      // Import versionedApiClient directly to avoid circular dependencies
      const { versionedApiClient } = await import('../api/versionedClient');
      const { CLIENT_ENDPOINTS } = await import('../api/endpointMap');
      
      console.log('Calling DB API endpoint for clients:', CLIENT_ENDPOINTS.LIST_CLIENTS);
      
      // Make direct call to DB endpoint to correctly track missing endpoints
      const response = await versionedApiClient.get(
        CLIENT_ENDPOINTS.LIST_CLIENTS,
        {}
      );
      
      console.log('DB API response status:', response.status);
      console.log('DB API content type:', response.headers?.['content-type']);
      
      // Helpful response debugging
      if (!response.data) {
        console.warn('DB API returned empty data');
        return [];
      }
      
      if (typeof response.data === 'string') {
        // If somehow we're still getting string data, it's likely HTML
        console.error('DB API returned string instead of JSON:', response.data.substring(0, 200));
        return [];
      }
      
      console.log('DB API response type:', typeof response.data);
      
      if (Array.isArray(response.data)) {
        console.log(`DB API returned array with ${response.data.length} items`);
        return response.data;
      }
      
      if (response.data.clients && Array.isArray(response.data.clients)) {
        console.log(`DB API returned object with clients array (${response.data.clients.length} items)`);
        return response.data.clients;
      }
      
      // Handle unexpected response format by inspecting and logging it
      console.warn('DB API returned unexpected data format:',
        typeof response.data === 'object' ? 
          `Object with keys: ${Object.keys(response.data).join(', ')}` : 
          typeof response.data);
      
      // Fallback: try to make something useful out of whatever we got
      return response.data || [];
    } catch (error: any) {
      console.error('Error fetching DB clients:', error?.message);
      if (error?.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response content-type:', error.response.headers?.['content-type']);
      }
      return [];
    }
  }, []);

  /**
   * Get workflows for a client using database approach only
   * Memoized to prevent unnecessary re-renders
   */
  const getWorkflows = useCallback(async (clientId: string | number) => {
    try {
      if (typeof clientId === 'number') {
        // Import versionedApiClient directly to avoid circular dependencies
        const { versionedApiClient } = await import('../api/versionedClient');
        const { WORKFLOW_ENDPOINTS } = await import('../api/endpointMap');
        
        // Make direct call to DB endpoint to correctly track missing endpoints
        const response = await versionedApiClient.get(
          WORKFLOW_ENDPOINTS.LIST_WORKFLOWS,
          { client_id: clientId }
        );
        
        return response.data.workflows || [];
      } else {
        // Convert string clientId to number for database approach
        // This handles legacy cases where string IDs might still be passed
        const numericClientId = parseInt(clientId.toString(), 10);
        if (isNaN(numericClientId)) {
          console.error(`Cannot convert clientId "${clientId}" to number for database approach`);
          return [];
        }
        
        const { versionedApiClient } = await import('../api/versionedClient');
        const { WORKFLOW_ENDPOINTS } = await import('../api/endpointMap');
        
        const response = await versionedApiClient.get(
          WORKFLOW_ENDPOINTS.LIST_WORKFLOWS,
          { client_id: numericClientId }
        );
        
        return response.data.workflows || [];
      }
    } catch (error: any) {
      console.error(`Error fetching workflows for client ${clientId}:`, error);
      return [];
    }
  }, []);

  /**
   * Helper to convert a client ID to a string representation
   * Memoized to prevent unnecessary re-renders
   */
  const clientIdToString = useCallback((clientId: string | number): string => {
    if (typeof clientId === 'string') {
      return clientId;
    }
    // For database clients, we might need to look up the client_code
    return clientId.toString();
  }, []);

  /**
   * Helper to get the appropriate workflow ID parameter based on workflow object
   * Since we always use database approach, prefer external_id if available, otherwise use workflow_id
   * Memoized to prevent unnecessary re-renders
   */
  const getWorkflowIdParam = useCallback((workflow: any): string => {
    // For DB workflows, prefer external_id if available, otherwise use workflow_id
    return (workflow.external_id || workflow.workflow_id).toString();
  }, []);

  /**
   * Helper to convert a list of DB clients to a list of client IDs
   * This is useful for backward compatibility with components expecting string IDs
   * Memoized to prevent unnecessary re-renders and API call loops
   */
  const convertDbClientsToIds = useCallback((clients: any): string[] => {
    // Handle undefined or null
    if (!clients) {
      console.warn('convertDbClientsToIds received null or undefined input');
      return [];
    }
    
    // Handle non-array inputs with clients property
    if (!Array.isArray(clients) && clients.clients && Array.isArray(clients.clients)) {
      console.log('Converting clients object with clients property to string IDs');
      clients = clients.clients;
    }
    
    // Handle non-array inputs without clients property
    if (!Array.isArray(clients)) {
      console.warn('convertDbClientsToIds received non-array input:', 
        typeof clients === 'object' ? JSON.stringify(clients).substring(0, 100) + '...' : clients);
      return [];
    }
    
    // Now we can safely use map since we know clients is an array
    return clients.map(client => {
      // Handle if the array contains strings already
      if (typeof client === 'string') {
        return client;
      }
      
      // Handle if the array contains client objects
      if (client && typeof client === 'object') {
        return client.client_code || client.client_id?.toString() || '';
      }
      
      // Default fallback for unexpected items
      return '';
    }).filter(Boolean); // Filter out empty strings
  }, []);

  return {
    isUsingDatabaseApproach,
    setIsUsingDatabaseApproach,
    dbClientId,
    setDbClientId,
    getClients,
    getWorkflows,
    clientIdToString,
    getWorkflowIdParam,
    convertDbClientsToIds
  };
}
